/* eslint-disable no-template-curly-in-string */
import { FormsValidationLocalization } from "../types/graphql/generated";
import { CartErrorMsg, SubmitOrderErrorKeys } from "../types/error";

export const genericErrorMsgEnglish = "Something went wrong. Please try again.";

const NO_MATCH =
  "Oops, it looks like your email and password combination don't match!";

export const SignUpErrorMessages: { [key: string]: string } = {
  not_found: NO_MATCH,
  invalid_password: NO_MATCH,
  account_locked: "Your account has been locked.",
  unverified_email: "Please enter a valid email address.",
};

export const validationErrorsEnglish: FormsValidationLocalization = {
  dayRequiredErrorText: "Please select a day",
  emailRequiredErrorText: "Your email address is required",
  fieldRequiredErrorText: "This field cannot be left blank",
  invalidDateErrorText: "Please enter a valid date.",
  invalidPasswordErrorText: "Invalid password",
  invalidPhoneErrorText: "Invalid phone number",
  monthRequiredErrorText: "Please select a month",
  newPasswordRequiredErrorText:
    "Please enter a new password to use for your account",
  passwordRequiredErrorText: "Your password is required",
  requiredErrorText: "Required",
  signupPasswordRequiredErrorText:
    "Please enter a password to use for your account",
  wrongEmailErrorText: "Email address is not valid",
  yearRequiredErrorText: "Please select a year",
  invalidTaxIdErrorText: "Invalid Tax ID",
  invalidTaxIdLengthErrorText: "Must be exactly 10 digits",
  companyRequiredWithTaxIdErrorText:
    "${companyLabel} is required with ${taxIdLabel}",
  taxIdRequiredWithCompanyErrorText:
    "${taxIdLabel} is required with ${companyLabel}",
};

export const sentryIgnoreErrorsList = [
  // Random plugins/extensions
  "top.GLOBALS",
  "ResizeObserver loop limit exceeded",
  "Can't find variable: instantSearchSDKJSBridgeClearHighlight",
  "Can't find variable: _AutofillCallbackHandler",
  "Failed to fetch",
  "Load failed",
  "The network connection was lost.",
  "TypeError: Failed to fetch",
  "TypeError: NetworkError when attempting to fetch resource.",
  "TypeError: Load failed",
  "TypeError: The network connection was lost.",
  "TypeError: cancelled",
  "cancelled",
  // Request header fields too large (usually due to many tracking cookies)
  /Response not successful: Received status code 431/,
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "http://tt.epicplay.com",
  "Can't find variable: ZiteReader",
  "jigsaw is not defined",
  "ComboSearch is not defined",
  "http://loading.retry.widdit.com/",
  "atomicFindClose",
  // Facebook borked
  "fb_xd_fragment",
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
  // reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268
  "bmi_SafeAddOnload",
  "EBCallBackMessageReceived",
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  "conduitPage",
  "Network request failed",
  "Error: Document is ready and element #paypal-button does not exist",
  /Error: Request to post \/smart\/api\/payment(.*)$/,
  /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/,
  /Error: Invalid call to runtime\.sendMessage\(\)\. Tab not found\./,
  /Invariant: attempted to hard navigate to the same URL/,
];

export const sentryDenyUrlsList = [
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  // Browser extensions
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
];

/**
 * Cart-level validation errors that inform the shopper without preventing
 * checkout. Every other cart error is a hard blocker that submitOrder would
 * reject, so only add a key here once the backend accepts a submission that
 * still carries it.
 *
 * `alertType` is not a substitute for this list — some blocking errors (e.g.
 * purchaseQtyLimit) are rendered as warnings.
 */
export const nonBlockingCartErrorKeys: CartErrorMsg["errorKey"][] = [
  SubmitOrderErrorKeys.scheduledOrderOutOfStock,
];

type CartAndItemErrorMapKeys =
  | "guestCartContainsSubscription"
  | "minimumPurchaseAmount"
  | "minimumPurchaseQuantity"
  | "lowStock"
  | "purchaseQtyLimit"
  | "outOfStock"
  | "scheduledOrderOutOfStock"
  | "zipCodeRestrictionOnItems"
  | "itemUnavailable"
  | "insufficientLoyaltyBalance"
  | "invalidLoyaltyReward"
  | "invalidGiftCard"
  | "minSubscriptionPriceUpdate"
  | "shippingAddressValidation"
  | "subscriptionsNotAllowed"
  | "unverifiedEmail"
  | "variantBelowMinQuantity"
  | "variantExceedsMaximumQuantity";

export const cartAndItemErrorMap: {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [key in CartAndItemErrorMapKeys]: {
    title: string;
    note: string;
    cart: string;
    scheduledOrderCart?: string;
  };
} = {
  guestCartContainsSubscription: {
    cart:
      "There are subscription items in your cart that require a registered account. " +
      "To continue as a guest, please remove them from your cart.",
    title: "Account needed",
    note: "Subscriptions are only available for registered users. Please create an account to proceed.",
  },
  minimumPurchaseAmount: {
    cart: "You have not met the ${minValue} minimum for your order. Please update your cart to complete your order.",
    title: "",
    note: "",
  },
  minimumPurchaseQuantity: {
    cart: "Please ensure a minimum quantity of ${minValue} items to proceed with your purchase.",
    title: "",
    note: "",
  },
  lowStock: {
    cart:
      "There are items in your cart where the quantity selected is more " +
      "than we have in stock. Please adjust or remove these items to " +
      "continue.",
    title: "Only ${inventoryQty} Available",
    note: "Please update the quantity for this item or remove it from your cart to proceed.",
  },
  purchaseQtyLimit: {
    cart:
      "There are items in your cart where the quantity selected is more " +
      "than available for purchase. Please adjust or remove these items " +
      "to continue.",
    title: "Only ${purchaseQtyLimit} Available",
    note: "Please update the quantity for this item or remove it from your cart to proceed.",
  },
  outOfStock: {
    cart: "There are items in your cart that are temporarily out of stock. Please remove them to continue.",
    scheduledOrderCart:
      "You have temporarily out of stock items in your cart. Please remove them or select " +
      "a delivery date on or after ${scheduledOrderDeliveryDate}.",
    title: "Temporarily out of stock",
    note: "Please remove this product to proceed.",
  },
  // non-blocking: the requested delivery date makes this a scheduled order, so
  // sold-out items can stay in the cart and the order can still be placed
  scheduledOrderOutOfStock: {
    cart:
      "You have temporarily out of stock items in your cart. Their availability will be " +
      "determined closer to the selected delivery date.",
    title: "",
    note: "",
  },
  zipCodeRestrictionOnItems: {
    cart: "",
    title: "Not available in your area",
    note: "Please remove this product to proceed.",
  },
  itemUnavailable: {
    cart: "There are items unavailable for purchase in your cart. Please remove them to continue.",
    title: "Not available",
    note: "Please remove this product to proceed.",
  },
  insufficientLoyaltyBalance: {
    cart: "",
    title: "Not enough points",
    note:
      "Your total points are not enough to complete this purchase." +
      " Please remove points redemption items to continue.",
  },
  invalidLoyaltyReward: {
    cart: "",
    title: "Loyalty reward has expired",
    note:
      "A loyalty reward was applied to your order and has expired. " +
      "Please go back and select another reward to proceed with checkout.",
  },
  invalidGiftCard: {
    cart: "",
    title: "There was an issue with your gift card",
    note: "Please re-enter the information and try again.",
  },
  minSubscriptionPriceUpdate: {
    note: "Please add more items to update your subscription.",
    title: "Product subtotal below $18",
    cart: "",
  },
  variantBelowMinQuantity: {
    cart:
      "There are items in your cart where the quantity selected is less " +
      "than the minimum. Please adjust or remove these items to continue.",
    title: "Minimum quantity of ${minQty} not met",
    note: "Please update the quantity for this item or remove it from your cart to proceed.",
  },
  variantExceedsMaximumQuantity: {
    cart:
      "There are items in your cart where the quantity selected is more " +
      "than the maximum. Please adjust or remove these items to continue.",
    title: "Maximum quantity of ${maxQty} exceeded",
    note: "Please update the quantity for this item or remove it from your cart to proceed.",
  },
  shippingAddressValidation: {
    cart: "We are unable to ship to this address, please choose a different address.",
    title: "",
    note: "",
  },
  unverifiedEmail: {
    cart: "We were unable to verify your email address, please double-check and try again.",
    title: "",
    note: "",
  },
  subscriptionsNotAllowed: {
    cart: "There are subscription items in your cart. Subscriptions are not available for your account. To continue, please remove them.",
    title: "Subscription unavailable",
    note: "Please remove this product to proceed.",
  },
};
